import React, { useState, useCallback } from 'react';
import { Button, Modal } from 'react-bootstrap';
import BlobDocViewer from '../BlobDocViewer';

export interface Attachment {
  id?: string;
  file_name?: string;
  content_type?: string;
  size?: number;
  note?: string;
}

interface AttachmentViewerProps {
  attachments: Attachment[];
  downloadFn: (attachmentId: string) => Promise<Blob>;
  title?: string;
}

const fileIcon = (contentType?: string) => {
  if (!contentType) return 'bi-file-earmark';
  if (contentType.startsWith('image/')) return 'bi-file-earmark-image';
  if (contentType === 'application/pdf') return 'bi-file-earmark-pdf';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'bi-file-earmark-spreadsheet';
  if (contentType.includes('word') || contentType.includes('document')) return 'bi-file-earmark-word';
  return 'bi-file-earmark';
};

const formatSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// File types we deliver as a browser download instead of rendering inline,
// either because the bundled viewer doesn't render them well or because the
// user is better off opening them in a native app (spreadsheets, etc.).
const isDownloadOnly = (att: Attachment): boolean => {
  if (att.content_type === 'text/csv') return true;
  const ext = att.file_name?.split('.').pop()?.toLowerCase();
  return ext === 'csv';
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({ attachments, downloadFn, title = 'Attachments' }) => {
  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  // Object URL for the currently-viewed attachment, surfaced by BlobDocViewer so
  // the footer can offer a Download link for the already-fetched blob.
  const [viewBlobUrl, setViewBlobUrl] = useState<string | null>(null);

  const openAttachment = useCallback(async (att: Attachment) => {
    if (!att.id) return;

    // Types we don't render inline get a plain browser download — still routed
    // through downloadFn so auth + error handling match the inline path.
    if (isDownloadOnly(att)) {
      try {
        const rawBlob = await downloadFn(att.id);
        triggerBrowserDownload(new Blob([rawBlob], { type: att.content_type || rawBlob.type }), att.file_name || 'attachment');
      } catch (err) {
        console.error('Failed to download attachment:', err);
      }
      return;
    }

    setViewingAttachment(att);
  }, [downloadFn]);

  const closeAttachment = useCallback(() => {
    setViewingAttachment(null);
    setViewBlobUrl(null);
  }, []);

  if (attachments.length === 0) return null;

  return (
    <>
      <div className="mt-3">
        <h6 className="fw-semibold">{title} ({attachments.length})</h6>
        <div className="d-flex flex-wrap gap-2">
          {attachments.map((att) => (
            <Button
              key={att.id}
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => openAttachment(att)}
            >
              <i className={`bi ${fileIcon(att.content_type)}`}></i>
              {att.file_name}
              {att.size ? <span className="text-muted ms-1">({formatSize(att.size)})</span> : null}
            </Button>
          ))}
        </div>
      </div>

      {viewingAttachment && (
        <Modal show={!!viewingAttachment} onHide={closeAttachment} size="xl" scrollable dialogClassName="attachment-viewer-modal">
          <style>{`.attachment-viewer-modal .modal-content { height: calc(100vh - 3.5rem); }`}</style>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">{viewingAttachment.file_name}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ overflow: 'hidden', padding: 0, background: '#e9ecef' }}>
            <BlobDocViewer
              sourceKey={viewingAttachment.id}
              fetcher={() => downloadFn(viewingAttachment.id!)}
              fileName={viewingAttachment.file_name}
              contentType={viewingAttachment.content_type}
              onBlobUrl={setViewBlobUrl}
              style={{ height: '100%', minHeight: 500 }}
            />
          </Modal.Body>
          <Modal.Footer>
            {viewBlobUrl && (
              <a
                href={viewBlobUrl}
                download={viewingAttachment.file_name}
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-download me-1"></i>Download
              </a>
            )}
            {viewingAttachment.note && (
              <span className="text-muted small me-auto">{viewingAttachment.note}</span>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default AttachmentViewer;
